import { Loader2, RefreshCw } from "lucide-react";

import { CopyToClipboardGlyph } from "@/components/ui/CopyToClipboardGlyph";
import { Button } from "@/components/ui/Button";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";
import { cn } from "@/lib/utils";

import {
  readPolledDeviceFirmware,
  readPolledDeviceImei,
  readPolledDeviceModelNumber,
  readPolledDeviceMcuUuid,
} from "./configurationDeviceFields";

function InfoField({
  label,
  value,
  mono,
  copyLabel,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyLabel: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 flex items-start gap-1 text-slate-900">
        <span className={cn(mono ? "min-w-0 break-all font-mono" : "min-w-0")}>{value || "—"}</span>
        <CopyToClipboardGlyph text={value} label={copyLabel} className="mt-0.5 shrink-0" />
      </dd>
    </div>
  );
}

export function ConfigurationDevicePolledPanel({
  activeDevice,
  showImei,
  connectorId,
  refreshPending,
  refreshNotice,
  onRefreshConnector,
  refreshDisabled,
  refreshEnabled,
}: {
  activeDevice: SdTestDevice | null;
  showImei: boolean;
  connectorId: string;
  refreshPending: boolean;
  refreshNotice: { text: string; tone: "error" | "warning" | "success" } | null;
  onRefreshConnector: () => void;
  refreshDisabled?: boolean;
  /** When true, refresh is allowed even if no device is currently active (SDTestApp connected). */
  refreshEnabled?: boolean;
}): JSX.Element {
  const polledSku = readPolledDeviceModelNumber(activeDevice);
  const polledFirmware = readPolledDeviceFirmware(activeDevice);
  const polledMcuUuid = readPolledDeviceMcuUuid(activeDevice);
  const polledImei = readPolledDeviceImei(activeDevice);
  const canRefresh = refreshEnabled === true;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Cable</p>
          <p className="mt-0.5 break-all font-mono text-sm text-slate-900">{connectorId.trim() || "—"}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-9 shrink-0 bg-white"
          disabled={!canRefresh || refreshPending || refreshDisabled === true}
          onClick={onRefreshConnector}
          title="Refresh connector — reconnect the device on this cable now"
        >
          {refreshPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          <span className="sr-only">Refresh connector</span>
        </Button>
      </div>
      {refreshNotice ? (
        <p
          className={cn(
            "text-xs",
            refreshNotice.tone === "error"
              ? "text-red-700"
              : refreshNotice.tone === "warning"
                ? "text-amber-800"
                : "text-emerald-800",
          )}
          role={refreshNotice.tone === "error" ? "alert" : "status"}
        >
          {refreshNotice.text}
        </p>
      ) : null}
      <dl className="space-y-3 text-sm">
        <InfoField label="SKU" value={polledSku} mono copyLabel="SKU" />
        <InfoField label="Firmware" value={polledFirmware} mono copyLabel="firmware" />
        <InfoField label="MCU UUID" value={polledMcuUuid} mono copyLabel="MCU UUID" />
        {showImei ? <InfoField label="IMEI" value={polledImei} mono copyLabel="IMEI" /> : null}
      </dl>
    </div>
  );
}
